import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Users, Package, Sparkles, Calendar, Settings as SettingsIcon, Bell, X, Info, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import InteractiveTour from "@/components/InteractiveTour";
import BiometricSetupModal from "@/components/BiometricSetupModal";
import { isPlatformAuthenticatorAvailable, getBiometricStatus, isBiometricSupported } from "@/utils/biometric";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import { userStorage } from '../utils/userStorage';
const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const businessAreas = [
  {
    title: "Kunden",
    icon: Users,
    path: "/kunden",
    description: "Kundenverwaltung & Kontakte",
    color: "from-emerald-500 to-teal-600",
    enabled: true,
    dataTour: "kunden-button"
  },
  {
    title: "Zielplanungs-Kalender",
    icon: Calendar,
    path: "/zielplanung",
    description: "Ziele, Termine & Gewohnheiten planen",
    color: "from-violet-500 to-purple-600",
    enabled: true,
    dataTour: "zielplanung-button"
  },
  {
    title: "Inventar",
    icon: Package,
    path: "/dashboard",
    description: "Warenverwaltung & Rechnungen",
    color: "from-blue-500 to-cyan-600",
    enabled: true,
    dataTour: "inventory-button"
  },
  {
    title: "Affirmation",
    icon: Sparkles,
    path: "#",
    description: "Kommt bald...",
    color: "from-slate-300 to-slate-400",
    enabled: false
  }
];

export default function MainHub() {
  const [showNachrichten, setShowNachrichten] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showTourManual, setShowTourManual] = useState(false);
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [showInstallTip, setShowInstallTip] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);

  useEffect(() => {
    loadNotifications();
    loadUserData();
    checkBiometricSetup();

    // App-Installations-Tipp anzeigen? – nicht während der Onboarding-Tour
    const dismissed = localStorage.getItem('burningteam_install_tip_dismissed');
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    const isTourLaunching = !!localStorage.getItem('burningTeamTour');
    if (!dismissed && !isStandalone && !isTourLaunching) {
      setShowInstallTip(true);
      if (window.pwaInstallPrompt) setInstallPrompt(window.pwaInstallPrompt);
    }
    const handlePrompt = (e) => {
      e.preventDefault();
      window.pwaInstallPrompt = e;
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handlePrompt);
    window.addEventListener('appinstalled', () => setShowInstallTip(false));
    return () => {
      window.removeEventListener('beforeinstallprompt', handlePrompt);
    };
  }, []);

  const handleInstallApp = async () => {
    const prompt = installPrompt || window.pwaInstallPrompt;
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') { setShowInstallTip(false); window.pwaInstallPrompt = null; }
    }
  };

  const handleDismissInstallTip = () => {
    localStorage.setItem('burningteam_install_tip_dismissed', '1');
    setShowInstallTip(false);
  };

  const checkBiometricSetup = async () => {
    // Don't show if already skipped in this session
    if (sessionStorage.getItem('biometric_setup_skipped')) return;

    // Don't show during onboarding/tour: warten bis User die Tour abgeschlossen hat
    if (window.burningTeamTourActive) return;
    try {
      const me = await axios.get(`${API}/auth/me`);
      if (me.data && me.data.tour_completed === false) return;
    } catch { /* ignore — falls /me fehlschlägt, Modal lieber nicht zeigen */ return; }

    // Don't show if already prompted today
    const lastPrompt = localStorage.getItem('biometric_prompt_date');
    const today = new Date().toDateString();
    if (lastPrompt === today) return;
    
    // Check if biometric is supported
    if (!isBiometricSupported()) return;
    const platformAvailable = await isPlatformAuthenticatorAvailable();
    if (!platformAvailable) return;
    
    // Check if user already has biometric setup
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const status = await getBiometricStatus(token);
    if (!status.biometric_enabled) {
      // Mark that we prompted today
      localStorage.setItem('biometric_prompt_date', today);
      // Small delay to let the page load first
      setTimeout(() => setShowBiometricSetup(true), 1500);
    }
  };

  useEffect(() => {
    loadNotifications();
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const response = await axios.get(`${API}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der Benutzerdaten:", error);
    }
    // Admin-Status separat laden (still, 403 ist kein Fehler)
    try {
      const admin = await axios.get(`${API}/api/admin/is-admin`);
      setIsAdmin(!!admin.data?.is_admin);
    } catch (e) {
      setIsAdmin(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const params = {};
      try {
        const ct = JSON.parse(userStorage.getItem('burningteam_contact_thresholds') || '{}');
        if (ct.yellowEnabled === false) params.yellow_enabled = false;
        else params.yellow_days = ct.yellowDays ?? 30;
        if (ct.redEnabled === false) params.red_enabled = false;
        else params.red_days = ct.redDays ?? 60;
      } catch {}
      const response = await axios.get(`${API}/api/nachrichten`, { params });
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error("Fehler beim Laden der Benachrichtigungen:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex justify-between items-center gap-3">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl md:text-3xl font-bold text-slate-800 truncate">Mein Business</h1>
              <p className="text-slate-500 mt-1 text-xs md:text-base hidden sm:block">Wählen Sie einen Bereich aus</p>
            </div>
            <div className="flex gap-1.5 md:gap-2 items-center flex-shrink-0">
              {/* Info Button für Tour */}
              <Button 
                onClick={() => setShowTourManual(true)}
                variant="outline"
                size="icon"
                className="border-emerald-300 hover:bg-emerald-50 h-9 w-9 md:h-10 md:w-10"
                title="App-Führung starten"
                data-tour="info-button"
              >
                <Info className="w-4 h-4 text-emerald-600" />
              </Button>
              
              <Button 
                onClick={() => setShowNachrichten(true)}
                variant="outline"
                size="icon"
                className="border-slate-300 hover:bg-slate-50 relative h-9 w-9 md:h-10 md:w-10"
                data-tour="nachrichten-button"
              >
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {unreadCount}
                  </span>
                )}
              </Button>
              <Link to="/settings">
                <Button 
                  data-testid="settings-button"
                  data-tour="einstellungen-button"
                  variant="outline"
                  size="icon"
                  className="border-slate-300 hover:bg-slate-50 h-9 w-9 md:h-10 md:w-10"
                >
                  <SettingsIcon className="w-4 h-4" />
                </Button>
              </Link>
              {/* Logo - rechts neben den Buttons */}
              <Link to="/main-hub" title="Zu Mein Business">
                <img 
                  src="/burningteam-logo.png" 
                  alt="BurningTeam" 
                  className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {businessAreas.map((area, index) => (
            area.enabled ? (
              <Link 
                key={index} 
                to={area.path}
                data-testid={`area-${area.title.toLowerCase()}`}
                data-tour={area.dataTour}
                className="animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                onClick={() => {
                  // Mark that we're coming from MainHub - Dashboard should scroll to top
                  sessionStorage.setItem('cameFromMainHub', 'true');
                  sessionStorage.removeItem('dashboardScrollPosition');
                }}
              >
                <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-slate-200 hover:border-slate-300 h-full transform hover:scale-105 hover:-translate-y-2 active:scale-[0.98] active:translate-y-0 cursor-pointer">
                  <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${area.color}`}></div>
                  
                  <div className="p-8 text-center">
                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${area.color} flex items-center justify-center mb-6 mx-auto group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg group-hover:shadow-xl`}>
                      <area.icon className="w-10 h-10 text-white" />
                    </div>
                    
                    <h3 className="text-2xl font-semibold text-slate-800 mb-3 group-hover:text-emerald-600 transition-colors">
                      {area.title}
                    </h3>
                    <p className="text-slate-500 text-base leading-relaxed">
                      {area.description}
                    </p>
                  </div>

                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-slate-100 opacity-0 group-hover:opacity-50 transition-opacity duration-300 pointer-events-none"></div>
                </div>
              </Link>
            ) : (
              <div 
                key={index}
                data-testid={`area-${area.title.toLowerCase()}`}
                className="animate-fade-in-up opacity-60 cursor-not-allowed"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative bg-slate-100 rounded-2xl shadow-md overflow-hidden border border-slate-300 h-full">
                  <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${area.color}`}></div>
                  
                  <div className="p-8 text-center">
                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${area.color} flex items-center justify-center mb-6 mx-auto shadow-md`}>
                      <area.icon className="w-10 h-10 text-white" />
                    </div>
                    
                    <h3 className="text-2xl font-semibold text-slate-500 mb-3">
                      {area.title}
                    </h3>
                    <p className="text-slate-400 text-base leading-relaxed">
                      {area.description}
                    </p>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>

        {/* Admin-Panel - nur für Admins (siehe backend ADMIN_USER_IDS) */}
        {isAdmin && (
          <div className="mt-10 max-w-5xl mx-auto animate-fade-in-up">
            <Link
              to="/admin"
              data-testid="admin-panel-button"
              className="block group"
            >
              <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-slate-700 hover:border-amber-400 transform hover:scale-[1.01] cursor-pointer">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500" />
                <div className="p-6 flex items-center gap-5">
                  <div className="w-14 h-14 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-400/20 transition-colors">
                    <ShieldCheck className="w-7 h-7 text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-amber-300">Admin</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-300">nur für dich sichtbar</span>
                    </div>
                    <h3 className="text-xl font-semibold text-white group-hover:text-amber-300 transition-colors">
                      App-Nutzungs-Übersicht
                    </h3>
                    <p className="text-slate-400 text-sm mt-0.5">
                      Aktive User, Neuanmeldungen, Online-Status & Analyse
                    </p>
                  </div>
                  <span className="text-amber-400 text-2xl transform group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </div>
            </Link>
          </div>
        )}
      </div>

      {/* App-Installations-Tipp – nur wenn keine Tour aktiv */}
      {showInstallTip && !showTourManual && (
        <div className="max-w-5xl mx-auto px-6 pb-8">
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4 flex items-start gap-4">
            <span className="text-3xl mt-0.5 flex-shrink-0">📱</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-emerald-900">
                Tipp: Du kannst Burning Team als App runterladen.{' '}
                <button
                  onClick={handleInstallApp}
                  className="underline decoration-2 text-emerald-700 hover:text-emerald-900 font-bold transition-colors"
                >
                  Jetzt installieren →
                </button>
              </p>
              <p className="text-xs text-emerald-700 mt-1">
                Das ist auch jederzeit unter{' '}
                <Link to="/settings" className="underline font-semibold hover:text-emerald-900">
                  Einstellungen
                </Link>{' '}
                möglich.
              </p>
              <label className="flex items-center gap-2 mt-3 cursor-pointer w-fit">
                <input
                  type="checkbox"
                  onChange={(e) => { if (e.target.checked) handleDismissInstallTip(); }}
                  className="w-4 h-4 accent-emerald-600 cursor-pointer"
                />
                <span className="text-xs text-emerald-700 select-none">Nicht mehr anzeigen</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Nachrichten-Center Modal */}
      {showNachrichten && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-slate-200">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">📬 Nachrichten-Center</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {unreadCount} {unreadCount === 1 ? 'Benachrichtigung' : 'Benachrichtigungen'}
                </p>
              </div>
              <button
                onClick={() => setShowNachrichten(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {notifications.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 text-lg">Keine Benachrichtigungen</p>
                  <p className="text-slate-400 text-sm mt-2">Alle Tasks sind erledigt! 🎉</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => {
                    const getPriorityStyles = (priority) => {
                      switch(priority) {
                        case "critical":
                          return "bg-red-50 border-red-600 hover:bg-red-100";
                        case "high_red":
                          return "bg-red-50 border-red-600 hover:bg-red-100";
                        case "high":
                          return "bg-orange-50 border-orange-500 hover:bg-orange-100";
                        case "high_blue":
                          return "bg-blue-50 border-blue-500 hover:bg-blue-100";
                        case "warning":
                          return "bg-yellow-50 border-yellow-500 hover:bg-yellow-100";
                        case "medium_orange":
                          return "bg-orange-50 border-orange-500 hover:bg-orange-100";
                        case "medium":
                          return "bg-blue-50 border-blue-500 hover:bg-blue-100";
                        case "success":
                          return "bg-green-50 border-green-500 hover:bg-green-100";
                        default:
                          return "bg-slate-50 border-slate-400 hover:bg-slate-100";
                      }
                    };

                    return (
                      <div
                        key={notif.id}
                        className={`p-4 rounded-lg border-l-4 transition-all ${getPriorityStyles(notif.priority)} cursor-pointer`}
                        onClick={() => {
                          // Navigiere zur Kunden-Seite mit diesem Teampartner
                          window.location.href = `/kunden?highlight=${notif.teampartner_id}`;
                        }}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="font-semibold text-slate-800">{notif.title}</p>
                            <p className="text-sm text-slate-600 mt-1">{notif.message}</p>
                            <p className="text-xs text-slate-500 mt-2 font-medium">{notif.person}</p>
                            {notif.termin_thema && (
                              <p className="text-xs text-slate-400 mt-1">Thema: {notif.termin_thema}</p>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  // API-Call zum Backend
                                  await axios.post(`${API}/api/nachrichten/dismiss/${notif.id}`);
                                  // Entferne lokal
                                  setNotifications(notifications.filter(n => n.id !== notif.id));
                                  setUnreadCount(prev => Math.max(0, prev - 1));
                                } catch (error) {
                                  console.error("Fehler beim Verwerfen:", error);
                                }
                              }}
                              className="text-slate-400 hover:text-slate-600 text-xs underline"
                              title="Verwerfen"
                            >
                              Verwerfen
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {notifications.length > 0 && (
              <div className="border-t border-slate-200 p-4 bg-slate-50">
                <button
                  onClick={async () => {
                    try {
                      // API-Call zum Backend
                      await axios.post(`${API}/api/nachrichten/dismiss-all`);
                      // Entferne lokal
                      setNotifications([]);
                      setUnreadCount(0);
                    } catch (error) {
                      console.error("Fehler beim Markieren:", error);
                    }
                  }}
                  className="w-full py-2 px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors text-sm font-medium"
                >
                  Alle als gelesen markieren
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PWA Install Banner – auf Hauptseite, versteckt sich bei Tour-Start */}
      <PWAInstallBanner />

      {/* Interactive Tour */}
      <InteractiveTour 
        user={user}
        manualStart={showTourManual}
        onComplete={() => {
          if (user) {
            setUser({...user, tour_completed: true});
          }
          setShowTourManual(false);
        }} 
      />

      {/* Biometric Setup Modal */}
      <BiometricSetupModal
        isOpen={showBiometricSetup}
        onClose={() => setShowBiometricSetup(false)}
        token={localStorage.getItem('token')}
        onSuccess={() => {
          // Optionally refresh user data
          loadUserData();
        }}
      />
    </div>
  );
}
